import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Flask,
  House,
  TestTube,
  Scroll,
  Dna,
  Virus,
  Database,
  Package,
  ChartLine,
  ImageSquare,
  MagnifyingGlass,
  Gear,
  SignOut,
  List,
  X,
  Bell,
  Users,
  CalendarBlank,
  FolderSimple,
  BookOpen,
  ChatCircleDots,
  Warning,
  TreeStructure,
  Robot,
  Target,
  TrendDown,
  FileText,
  Kanban,
  Wrench,
  Drop,
  Snowflake,
  ListBullets
} from '@phosphor-icons/react';

const navGroups = [
  {
    label: 'Core',
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: House },
      { path: '/projects', label: 'Projects', icon: Kanban },
      { path: '/chat', label: 'Chat', icon: ChatCircleDots },
      { path: '/ai-assistant', label: 'AI Assistant', icon: Robot },
    ]
  },
  {
    label: 'Lab Work',
    items: [
      { path: '/experiments', label: 'Experiments', icon: TestTube },
      { path: '/protocols', label: 'Protocols', icon: Scroll },
      { path: '/plasmids', label: 'Plasmids', icon: Dna },
      { path: '/strains', label: 'Strains', icon: Virus },
      { path: '/samples', label: 'Samples', icon: Database },
    ]
  },
  {
    label: 'Inventory & Analysis',
    items: [
      { path: '/inventory', label: 'Inventory', icon: Package },
      { path: '/smart-inventory', label: 'Smart Inventory', icon: TrendDown },
      { path: '/equipment', label: 'Equipment', icon: Wrench },
      { path: '/ln2-storage', label: 'LN2 Storage', icon: Drop },
      { path: '/freezer80', label: '-80°C Freezer', icon: Snowflake },
      { path: '/sample-inventory', label: 'Sample Inventory', icon: ListBullets },
      { path: '/sequences', label: 'Sequences', icon: Dna },
      { path: '/crispr-designer', label: 'CRISPR Designer', icon: Target },
      { path: '/analysis', label: 'Data Analysis', icon: ChartLine },
    ]
  },
  {
    label: 'Tools',
    items: [
      { path: '/lineage', label: 'Lineage Graph', icon: TreeStructure },
      { path: '/failures', label: 'Failure Dashboard', icon: Warning },
      { path: '/figures', label: 'Figure Maker', icon: ImageSquare },
      { path: '/my-files', label: 'My Files', icon: FolderSimple },
      { path: '/research', label: 'Research', icon: BookOpen },
      { path: '/paper-to-protocol', label: 'Paper to Protocol', icon: FileText },
      { path: '/calendar', label: 'Calendar', icon: CalendarBlank },
    ]
  }
];

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Mobile menu button */}
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X size={20} /> : <List size={20} />}
      </button>

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} data-testid="sidebar">
        {/* Logo */}
        <div className="px-5 py-5" style={{ borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
          <NavLink to="/dashboard" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1, #06B6D4)', boxShadow: '0 0 20px rgba(99,102,241,0.25)' }}>
              <Flask size={18} weight="bold" className="text-white" />
            </div>
            <div>
              <span className="text-white font-bold text-sm tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Chaturvedi Lab</span>
              <span className="block text-[10px] tracking-wider uppercase" style={{ color: '#818CF8' }}>UC Riverside</span>
            </div>
          </NavLink>
        </div>

        {/* Navigation Groups */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto">
          {navGroups.map((group) => (
            <div key={group.label} className="mb-1">
              <span className="block px-3 py-2 text-[10px] tracking-[0.15em] uppercase font-semibold" style={{ color: '#64748B' }}>
                {group.label}
              </span>
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `sidebar-item ${isActive ? 'active' : ''}`
                  }
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon size={17} weight={location.pathname.startsWith(item.path) ? 'fill' : 'regular'} />
                  <span className="text-[13px]">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="px-3 py-3 space-y-0.5" style={{ borderTop: '1px solid rgba(148,163,184,0.08)' }}>
          {(user?.role === 'admin' || user?.role === 'pi') && (
            <NavLink
              to="/users"
              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
              data-testid="nav-users"
            >
              <Users size={17} />
              <span className="text-[13px]">Manage Users</span>
            </NavLink>
          )}
          <NavLink
            to="/settings"
            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
            data-testid="nav-settings"
          >
            <Gear size={17} />
            <span className="text-[13px]">Settings</span>
          </NavLink>
          <button
            onClick={handleLogout}
            className="sidebar-item w-full"
            style={{ color: 'rgba(248, 113, 113, 0.8)' }}
            data-testid="logout-btn"
          >
            <SignOut size={17} />
            <span className="text-[13px]">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {/* Top bar */}
        <header className="px-6 py-3.5 flex items-center justify-between sticky top-0 z-30" style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
          <form onSubmit={handleSearch} className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
              <input
                type="text"
                placeholder="Search experiments, plasmids, strains..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field w-full pl-9 py-2 text-sm"
                data-testid="global-search-input"
              />
            </div>
          </form>

          <div className="flex items-center gap-3 ml-4">
            <button className="p-2 rounded-lg transition-colors relative" style={{ color: '#94A3B8' }} data-testid="notifications-btn">
              <Bell size={18} />
            </button>
            <div className="flex items-center gap-2.5 pl-3" style={{ borderLeft: '1px solid rgba(148,163,184,0.12)' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm" style={{ background: 'linear-gradient(135deg, #6366F1, #06B6D4)' }}>
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium" style={{ color: '#F8FAFC' }} data-testid="user-name">{user?.name}</p>
                <p className="text-[11px] capitalize" style={{ color: '#94A3B8' }}>{user?.role === 'pi' ? 'Principal Investigator' : user?.role}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6 animate-fade-in">
          {children}
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
